import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { toastUtils } from "../../../Utils/helper";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import Button from "../../../components/common/Button/Button";
import images from "../../../images";

// Constants for reusability
const FILE_ICONS = {
  image: images.IconPNGImage,
  pdf: images.IconPDF,
  default: images.DefaultUploadImage,
};

const STYLES = {
  container:
    "flex flex-col items-center text-xs max-w-[480px] text-zinc-500 relative",
  label:
    "flex flex-col justify-center items-center w-full bg-white rounded-md min-h-[92px] cursor-pointer relative border-2 border-dashed",
  button: {
    base: "flex-1 shrink gap-2.5 px-5 py-4 rounded-lg w-full text-white text-base font-medium border-none mt-4",
    disabled: "bg-[#ECEEFF] cursor-not-allowed",
    enabled: "bg-[#253FF6] hover:bg-indigo-700",
  },
};

function UploadReceiptConntact({
  label = "Upload File",
  onFileUpload = () => {},
  maxFiles = 5,
  initialFiles = [],
  handleNextStep = () => {},
  setFileUploaded = () => {},
  isUploading = false,
  hideSubmitButton = false,
}) {
  const [files, setFiles] = useState([]);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState(null);
  const { t } = useTranslation();

  // Use useRef to track files for cleanup
  const filesRef = useRef(files);

  // Update filesRef whenever files changes
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    setFiles([]);
    setConfirmDeleteFile(null);

    if (Array.isArray(initialFiles) && initialFiles.length > 0) {
      const processedFiles = initialFiles.map((file, index) => {
        const isFileObject = file instanceof File;

        if (isFileObject) {
          return {
            id: `file_${index}_${Date.now()}`,
            name: file.name,
            type: "image",
            file: file,
            preview: URL.createObjectURL(file),
            isLocal: true,
            serverData: null,
          };
        }

        let fileName = "";
        let previewUrl = null;
        let serverData = file;
        if (file?.name) {
          fileName = file.name;
        } else if (file?.file?.name) {
          fileName = file.file.name;
        } else if (file?.file_name) {
          fileName = file.file_name;
        } else if (file?.filename) {
          fileName = file.filename;
        } else {
          fileName = `File_${index + 1}`;
        }
        if (file?.file_path) {
          previewUrl = `${process.env.REACT_APP_URL_IMAGE}/${file.file_path}`;
        } else if (file?.url) {
          previewUrl = file.url;
        } else if (file?.file?.file_path) {
          previewUrl = `${process.env.REACT_APP_URL_IMAGE}/${file.file.file_path}`;
        } else if (file?.file?.url) {
          previewUrl = file.file.url;
        }

        return {
          id: `server_${index}_${Date.now()}`,
          name: fileName,
          type: "image",
          file: null,
          preview: previewUrl,
          isLocal: false,
          serverData: serverData,
        };
      });

      setFiles(processedFiles);
    }

    // Cleanup function for local preview URLs
    return () => {
      filesRef.current.forEach((file) => {
        if (file.preview && file.isLocal && file.preview.startsWith("blob:")) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [initialFiles]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const imageFiles = selectedFiles.filter((file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        toastUtils.error(`${file.name} ${t("toast.error.isNotAnImage")}`);
      }
      return isImage;
    });

    if (imageFiles.length === 0) return;

    if (imageFiles.length + files.length > maxFiles) {
      toastUtils.warning(
        `${t("uploadComponent.youCanOnlyUploadAMaximumOf")} ${maxFiles} ${t(
          "uploadComponent.file"
        )}`
      );
      return;
    }

    // Create new files with preview
    const newFiles = imageFiles.map((file, index) => ({
      id: `new_${Date.now()}_${index}`,
      name: file.name,
      type: "image",
      file,
      preview: URL.createObjectURL(file),
      isLocal: true,
      serverData: null,
    }));

    // Add to existing files
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);

    // Notify parent component
    onFileUpload(
      updatedFiles.map((f) => f.file || f.serverData).filter(Boolean)
    );

    // Reset input value
    event.target.value = "";
  };

  const handleDeleteFile = (fileId) => {
    const fileToRemove = files.find((file) => file.id === fileId);
    const updatedFiles = files.filter((file) => file.id !== fileId);

    setFiles(updatedFiles);
    setConfirmDeleteFile(null);

    // Cleanup preview URL if it's a local file
    if (
      fileToRemove?.preview &&
      fileToRemove.isLocal &&
      fileToRemove.preview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    // Notify parent component
    onFileUpload(
      updatedFiles.map((f) => f.file || f.serverData).filter(Boolean)
    );
  };

  const handleCancelDelete = () => {
    setConfirmDeleteFile(null);
  };

  const handleSubmit = () => {
    if (files.length === 0) {
      toastUtils.error(t("toast.error.noFilesUploaded"));
      return;
    }
    setFileUploaded(true);
    handleNextStep();
  };

  return (
    <div className={STYLES.container}>
      <label className={STYLES.label}>
        <input
          type="file"
          className="hidden"
          onChange={handleFileChange}
          multiple
          accept="image/*"
        />
        <div className="flex flex-col items-center py-4 w-full mt-2">
          <img
            loading="lazy"
            src={FILE_ICONS.default}
            className="object-contain w-[26px]"
            alt="Default icon"
          />
          <div className="gap-1.5 self-stretch my-auto mt-1.5 flex justify-center text-[#A0A0A0] text-sm font-medium">
            {label}
          </div>
        </div>
      </label>

      {files.length === 0 ? (
        <div className="flex flex-col items-center"></div>
      ) : (
        <div className="w-full mt-5 flex flex-col gap-3">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex flex-col border-[1px] border-[#EEEAE8] relative rounded-[10px] overflow-hidden"
            >
              {confirmDeleteFile === file.id ? (
                <div className="absolute inset-0 bg-black/50 flex flex-col justify-center items-center z-10 p-4 gap-2">
                  <span className="text-white text-base font-medium text-center">
                    {t("quest.detailMultipleChoice.confirmDelete")}
                  </span>
                  <div className="flex gap-2 w-full justify-center">
                    <Button
                      text={t("base.delete")}
                      onClick={() => handleDeleteFile(file.id)}
                      className="flex-1 shrink gap-2.5 px-5 py-2 rounded-xl max-w-[110px] bg-white text-[#253FF6] text-base font-medium"
                    />
                    <Button
                      text={t("base.cancel")}
                      className="flex-1 shrink gap-2.5 px-5 py-2 rounded-xl max-w-[110px] text-white bg-[#253FF6] text-base font-medium border-none"
                      variant="primary"
                      onClick={handleCancelDelete}
                    />
                  </div>
                </div>
              ) : (
                <div className="absolute w-full flex justify-center top-8 z-10">
                  <img
                    src={images.TrashWhiteIcon}
                    onClick={() => setConfirmDeleteFile(file.id)}
                    className="text-white rounded-full p-1 w-8 flex items-center justify-center cursor-pointer"
                    alt="Delete icon"
                  />
                </div>
              )}

              <div className="relative">
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="w-full max-h-[100px] object-cover rounded-md"
                    onError={(e) => {
                      console.error("Image load error:", e, "File:", file);
                      e.target.style.display = "none";
                      const filenameElement = e.target.nextElementSibling;
                      if (filenameElement) {
                        filenameElement.style.display = "block";
                      }
                    }}
                  />
                ) : null}
                <div
                  className="p-3 text-center text-sm text-gray-600 bg-gray-50"
                  style={{
                    display: file.preview ? "none" : "block",
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={FILE_ICONS.image}
                      alt="File icon"
                      className="w-8 h-8"
                    />
                    <span className="break-all">{file.name}</span>
                    <span className="text-xs text-gray-400">
                      {file.isLocal ? "Local file" : "Uploaded file"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!hideSubmitButton && (
        <button
          onClick={handleSubmit}
          className={`${STYLES.button.base} ${
            isUploading || files.length === 0
              ? STYLES.button.disabled
              : STYLES.button.enabled
          }`}
          disabled={isUploading || files.length === 0}
        >
          {isUploading ? (
            <span className="flex items-center justify-center">
              <BeatLoader size={5} loading={true} color="#3534ff" />
            </span>
          ) : (
            t("base.submit")
          )}
        </button>
      )}
    </div>
  );
}

UploadReceiptConntact.propTypes = {
  label: PropTypes.string,
  onFileUpload: PropTypes.func,
  maxFiles: PropTypes.number,
  initialFiles: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.instanceOf(File),
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        file: PropTypes.instanceOf(File),
        file_path: PropTypes.string,
      }),
    ])
  ),
  handleNextStep: PropTypes.func,
  setFileUploaded: PropTypes.func,
  isUploading: PropTypes.bool,
  hideSubmitButton: PropTypes.bool,
};

export default UploadReceiptConntact;
